import React from 'react';
import { Area, AreaChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { FearGreedData, FearGreedMeta } from '../useFearGreed';
import { ZONE_INFO, zoneFearGreed } from './FearGreedGauge';

interface FearGreedPanelProps {
    data: FearGreedData;
    meta: FearGreedMeta | null;
    theme: Record<string, string>;
}

const formatDate = (iso: string): string => {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y.slice(2)}`;
};

/** Détail de l'indice : historique et cinq composantes. Descriptif, jamais une recommandation. */
export const FearGreedPanel: React.FC<FearGreedPanelProps> = ({ data, meta, theme }) => (
    <div style={{ padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ margin: 0, fontSize: 16, color: theme.text }}>Indice Peur &amp; Avidité — historique 1 an</h3>
            <span style={{ fontSize: 11, color: theme.sub }}>
                Rodage admin · méthode {meta?.methodo_version ?? '—'} · {meta?.nb_titres_univers ?? '—'} titres échangés le {formatDate(data.date)}
            </span>
        </div>

        <div style={{ width: '100%', height: 220, marginTop: 12 }}>
            <ResponsiveContainer width='100%' height='100%'>
                <AreaChart data={data.historique} margin={{ top: 5, right: 10, bottom: 0, left: -20 }}>
                    <CartesianGrid strokeDasharray='3 3' stroke={theme.border} />
                    <XAxis dataKey='date' tickFormatter={formatDate} tick={{ fontSize: 11, fill: theme.sub }} minTickGap={40} />
                    <YAxis domain={[0, 100]} ticks={[0, 25, 45, 55, 75, 100]} tick={{ fontSize: 11, fill: theme.sub }} />
                    <ReferenceLine y={25} stroke={ZONE_INFO.PEUR_EXTREME.color} strokeDasharray='4 4' />
                    <ReferenceLine y={75} stroke={ZONE_INFO.AVIDITE_EXTREME.color} strokeDasharray='4 4' />
                    <Tooltip
                        labelFormatter={label => formatDate(String(label))}
                        formatter={value => {
                            const score = Number(value);
                            return [`${Math.round(score)} · ${ZONE_INFO[zoneFearGreed(score)].label}`, 'Indice'];
                        }}
                        contentStyle={{ background: theme.boxBg, border: `1px solid ${theme.border}`, fontSize: 12 }}
                    />
                    <Area type='monotone' dataKey='score' stroke='#3b82f6' fill='#3b82f6' fillOpacity={0.12} strokeWidth={2} dot={false} />
                </AreaChart>
            </ResponsiveContainer>
        </div>

        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            {data.composantes.map(c => {
                const zone = c.score != null ? ZONE_INFO[zoneFearGreed(c.score)] : null;
                return (
                    <div key={c.cle} title={c.description} style={{ border: `1px solid ${theme.border}`, borderRadius: 8, padding: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                            <span style={{ color: theme.text, fontWeight: 600 }}>{c.libelle}</span>
                            <span style={{ color: zone?.color ?? theme.sub, fontWeight: 700 }}>
                                {c.score != null ? Math.round(c.score) : '—'}
                            </span>
                        </div>
                        <div style={{ height: 6, borderRadius: 3, background: theme.border, marginTop: 6, overflow: 'hidden' }}>
                            <div style={{ width: `${c.score ?? 0}%`, height: '100%', background: zone?.color ?? theme.sub }} />
                        </div>
                        <div style={{ fontSize: 11, color: theme.sub, marginTop: 6 }}>{c.description}</div>
                    </div>
                );
            })}
        </div>

        {meta?.avertissement && (
            <p style={{ fontSize: 11, color: theme.sub, marginTop: 14, marginBottom: 0 }}>{meta.avertissement}</p>
        )}
    </div>
);
